/**
 * @deprecated This component has been replaced by `FullSizeAssemblySection` as
 * part of the `ScaleContainer` implementation. It is kept temporarily for
 * backwards-compatibility and will be removed in a future tech debt cleanup.
 */
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  ImportExport as ImportExportIcon,
} from '@mui/icons-material';
import { Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { SECTION_NAME_FONT } from 'src/constants';
import {
  getZIndexStylesFromSectionStyle,
  handleEventStyle,
} from 'src/features/assemblies/editAssemblyPage/EditAssemblyPage.helper';
import { ComponentTypes, DynamicSectionEvents } from 'src/interfaces';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import { GcfComponentSection } from '../gcfComponentSection';
import { AssemblyScreenSectionProps } from './AssemblyScreenSection.interface';
import {
  AssemblyScreenSectionContainer,
  AssemblyScreenSectionIconContainer,
  AutoAwesomeMosaicIconStyled,
  AutorenewIconStyled,
  OriginalAssemblyIconContainer,
  RotateIconContainer,
  SubdirectoryArrowRightIconStyled,
} from './AssemblyScreenSection.styled';

export const AssemblyScreenSection = ({
  scale = 2,
  showEditIcon,
  showReplaceIcon,
  showWarningIcon,
  selectable,
  isSelected,
  assemblyComponents,
  showParentIcon,
  showChildIcon,
  onClick,
  activeAssemblyJourney,
  section,
  sectionTypes,
  isCodConfirmationOpen,
  isLayoutList,
  isDisabled,
  sectionName,
  type,
  customerState,
  showSectionBorder = true,
  productFilters,
  productVariationFilters,
  skipProductsApiOnEmptyFilters,
}: AssemblyScreenSectionProps) => {
  const screenStyle = handleEventStyle(
    activeAssemblyJourney,
    section.config?.events,
    scale,
    sectionTypes,
  );

  const iconZIndex = getZIndexStylesFromSectionStyle(screenStyle);
  const isCodComponent = section.config?.suggestedComponentTypes?.includes(
    ComponentTypes.COD,
  );
  const isCodConfirmationActive = isCodComponent && isCodConfirmationOpen;

  // TODO: Updating size of section which will display COD to center the icon when cod confirmation is displayed. Need to refactor the logic to handel size updated for COD.
  const sectionHeight = isCodConfirmationActive ? 1920 : section.height;
  const sectionWidth = isCodConfirmationActive ? 1080 : section.width;

  // TODO: use if statement
  const bgColor = !isEmpty(assemblyComponents)
    ? 'secondary.main'
    : section.config?.dynamicHighlight! > 0
    ? 'brand.yellowGreen'
    : isDisabled
    ? 'brand.disabled'
    : isLayoutList
    ? 'brand.lightGreen'
    : 'primary.lighter';

  const selectedStyle = isSelected
    ? {
        borderWidth: '3px',
        borderStyle: 'solid',
        borderColor: 'brand.green',
      }
    : undefined;

  const gcfAssemblyComponents =
    assemblyComponents?.map((item) => {
      return {
        ...(item.id != null || item.uid != null
          ? { id: item.id ?? item.uid }
          : {}),
        ...(item.component_id != null
          ? { componentId: item.component_id }
          : {}),
        rotationOrder: item?.rotation_order,
        config: item?.config,
        backupOrder: item?.backup_order,
        component: {
          ...(item.component_id != null ? { id: item.component_id } : {}),
          type: item?.component?.component_type?.name,
          config: item.component?.config,
          name: item.component?.name,
          width: item.component?.width,
          height: item.component?.height,
          theme: item.component?.theme?.name,
        },
      };
    }) ?? [];

  return (
    <AssemblyScreenSectionContainer
      width={sectionWidth}
      height={sectionHeight}
      $leftOffset={section.left_offset}
      $topOffset={section.top_offset}
      $scale={scale}
      $selectable={isSelected ? false : selectable}
      $isSelected={isSelected}
      $screenStyle={screenStyle}
      $initialStyle={section.config?.initial?.style}
      type={type}
      onClick={selectable ? onClick : undefined}
      bgcolor={bgColor}
      $showSectionBorder={showSectionBorder}
      data-testid="assembly-screen-section">
      {!isEmpty(assemblyComponents) && (
        <>
          {assemblyComponents &&
            assemblyComponents.filter((item) => item.backup_order === 0)
              .length > 1 && (
              <RotateIconContainer $zIndex={iconZIndex}>
                <AutorenewIconStyled color="primary" />
              </RotateIconContainer>
            )}
          {showParentIcon && (
            <OriginalAssemblyIconContainer $zIndex={iconZIndex}>
              <AutoAwesomeMosaicIconStyled />
            </OriginalAssemblyIconContainer>
          )}
          {showChildIcon && (
            <OriginalAssemblyIconContainer $zIndex={iconZIndex}>
              <SubdirectoryArrowRightIconStyled />
            </OriginalAssemblyIconContainer>
          )}
          {isCodComponent ? (
            <GcfComponentSection
              screenSectionId={section.screenSectionId}
              assemblyComponents={gcfAssemblyComponents}
              scale={isSelected && scale ? scale * 0.98 : scale}
              height={sectionHeight}
              width={sectionWidth}
              previewData={getSectionPreviewData(
                assemblyComponents?.[0].component?.component_type
                  ?.name as ComponentTypes,
                isCodConfirmationOpen
                  ? DynamicSectionEvents.COD_CONFIRMATION_OPEN
                  : DynamicSectionEvents.COD_ORDER_OPEN,
                customerState,
              )}
              containerStyle={selectedStyle}
              productFilters={productFilters}
              productVariationFilters={productVariationFilters}
              skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}
            />
          ) : (
            <GcfComponentSection
              screenSectionId={section.screenSectionId}
              assemblyComponents={gcfAssemblyComponents}
              scale={isSelected && scale ? scale * 0.98 : scale}
              height={sectionHeight}
              width={sectionWidth}
              containerStyle={selectedStyle}
              productFilters={productFilters}
              productVariationFilters={productVariationFilters}
              skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}
            />
          )}
        </>
      )}

      {showEditIcon && (
        <>
          {showWarningIcon ? (
            <AssemblyScreenSectionIconContainer
              bgcolor="primary.lighter"
              $isVertical={section.height > section.width}>
              <ErrorIcon color="error" />
            </AssemblyScreenSectionIconContainer>
          ) : (
            <AssemblyScreenSectionIconContainer
              bgcolor="primary.lighter"
              $isVertical={section.height > section.width}>
              <EditIcon color="primary" />
            </AssemblyScreenSectionIconContainer>
          )}
        </>
      )}

      {sectionName && (
        <Typography
          variant="body1"
          sx={{ fontSize: `${scale * SECTION_NAME_FONT}rem` }}>
          {sectionName}
        </Typography>
      )}

      {showReplaceIcon && !isSelected && (
        <AssemblyScreenSectionIconContainer
          bgcolor="primary.lighter"
          $isVertical={section.height > section.width}>
          <ImportExportIcon color="primary" />
        </AssemblyScreenSectionIconContainer>
      )}

      {showReplaceIcon && isSelected && (
        <AssemblyScreenSectionIconContainer
          bgcolor="primary.lighter"
          $isVertical={section.height > section.width}>
          <CheckCircleOutlineIcon color="success" />
        </AssemblyScreenSectionIconContainer>
      )}
    </AssemblyScreenSectionContainer>
  );
};
